/**
 * Instrumentación de diagnóstico auth/MFA/rutas protegidas.
 * Incluye buffer en memoria + overlay en app (sin depender de DevTools).
 * Cierre del incidente iOS:
 * - desarrollo: disponible bajo activación explícita;
 * - producción pública: no-op seguro, sin overlay ni listeners activables por query params.
 *
 * Activación:
 * - Query: ?debugAuth=1
 * - localStorage: rc_auth_debug = "1" (tras recargar, o cuando el detector de activación detecte cambio periódico)
 *
 * NO registrar tokens, refresh tokens, OTP, contraseñas ni emails completos en payloads.
 */

const STORAGE_KEY = 'rc_auth_debug';
const QUERY_SUBSTRING = 'debugAuth=1';
const UI_STORAGE_KEY = 'rc_auth_debug_ui';
const UI_QUERY_SUBSTRING = 'debugAuthUi=1';
const AUTH_DEBUG_RUNTIME_ENABLED = import.meta.env.DEV;

const MAX_LOG_EVENTS = 200;

export type AuthDebugLogEntry = {
  id: number;
  epochMs: number;
  perfMsApprox: number;
  pathnameSnapshot: string;
  searchSnapshot: string;
  event: string;
  payload: Record<string, unknown>;
};

type OverlaySlices = Record<string, Record<string, unknown>>;

const listeners = new Set<() => void>();

let seq = 0;
const logRing: AuthDebugLogEntry[] = [];
let overlaySlices: OverlaySlices = {};
let latestRedirectReason: string | null = null;
let notifyScheduled = false;

export function subscribeAuthDebug(listener: () => void): () => void {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) {
    return () => {};
  }

  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function flushAuthDebugListeners(): void {
  notifyScheduled = false;
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // ignore diagnostics listener errors
    }
  }
}

function notifyAuthDebugListeners(): void {
  if (notifyScheduled) return;
  notifyScheduled = true;

  try {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(flushAuthDebugListeners);
      return;
    }
  } catch {
    // fall through
  }

  setTimeout(flushAuthDebugListeners, 0);
}

export function getAuthDebugLogEntries(): readonly AuthDebugLogEntry[] {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) return [];
  return logRing;
}

export function getAuthDebugOverlaySlices(): OverlaySlices {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) return {};
  return { ...overlaySlices };
}

export function getAuthDebugLatestRedirectReason(): string | null {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) return null;
  return latestRedirectReason;
}

export function isAuthDebugEnabled(): boolean {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === '1') return true;
    return window.location.search.includes(QUERY_SUBSTRING);
  } catch {
    return false;
  }
}

export function isAuthDebugUiEnabled(): boolean {
  if (!AUTH_DEBUG_RUNTIME_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage.getItem(UI_STORAGE_KEY) === '1') return true;
    return window.location.search.includes(UI_QUERY_SUBSTRING);
  } catch {
    return false;
  }
}

const SENSITIVE_KEY_RE =
  /password|passwd|pwd|secret|token|refresh|bearer|authorization|otp|totpcode|credential|session\.|refresh_token/i;

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (depth > 5) return '[max-depth]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (value.length > 200) return `[string:${value.length}chars]`;
    return value;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((entry) => sanitizeValue(entry, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (SENSITIVE_KEY_RE.test(key)) {
      out[key] = '[redacted]';
      continue;
    }
    out[key] = sanitizeValue(nested as unknown, depth + 1);
  }
  return out;
}

export function publishAuthDebugOverlay(sliceKey: string, data: Record<string, unknown>): void {
  if (!isAuthDebugEnabled()) return;

  const prev = overlaySlices[sliceKey] ?? {};
  overlaySlices = {
    ...overlaySlices,
    [sliceKey]: sanitizeValue({ ...prev, ...data, _overlayTsMs: Date.now() }) as Record<string, unknown>,
  };
  notifyAuthDebugListeners();
}

function readLocationSnapshot(): { pathnameSnapshot: string; searchSnapshot: string } {
  if (typeof window === 'undefined') {
    return { pathnameSnapshot: '', searchSnapshot: '' };
  }

  try {
    return {
      pathnameSnapshot: window.location.pathname,
      searchSnapshot:
        typeof window.location.search === 'string' && window.location.search.length > 200
          ? `${window.location.search.slice(0, 200)}…`
          : window.location.search || '',
    };
  } catch {
    return { pathnameSnapshot: '?', searchSnapshot: '?' };
  }
}

/** Evento registrado para overlay + consola opcional */
export function authDebug(event: string, payload?: Record<string, unknown>): void {
  if (!isAuthDebugEnabled()) return;

  const sanitizedPayload = (payload
    ? (sanitizeValue(payload) as Record<string, unknown>)
    : {}) as Record<string, unknown>;

  if (/^AdminProtectedRoute\.navigate$|^PublicLogin\.navigate\./i.test(event)) {
    const rawReason =
      sanitizedPayload.reason != null && typeof sanitizedPayload.reason === 'string'
        ? sanitizedPayload.reason
        : null;
    const toHint =
      typeof sanitizedPayload.toPath === 'string'
        ? sanitizedPayload.toPath
        : typeof sanitizedPayload.toPathPrefix === 'string'
          ? sanitizedPayload.toPathPrefix
          : '';

    latestRedirectReason = [event, rawReason, toHint].filter(Boolean).join('|');
  }

  let perfApprox = 0;
  try {
    perfApprox =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
  } catch {
    perfApprox = Date.now();
  }

  const { pathnameSnapshot, searchSnapshot } = readLocationSnapshot();
  const entry: AuthDebugLogEntry = {
    id: ++seq,
    epochMs: Date.now(),
    perfMsApprox: perfApprox,
    pathnameSnapshot,
    searchSnapshot,
    event,
    payload: sanitizedPayload,
  };

  logRing.push(entry);
  if (logRing.length > MAX_LOG_EVENTS) {
    logRing.splice(0, logRing.length - MAX_LOG_EVENTS);
  }

  notifyAuthDebugListeners();

  try {
    console.debug(`[rc-auth-debug] +${perfApprox.toFixed?.(1) ?? perfApprox}ms ${event}`, sanitizedPayload ?? {});
  } catch {
    // ignore console failures (Safari private mode quirks)
  }
}
